import { Warning, File } from "@phosphor-icons/react";
import Typography from "../Typography";
import { formatDateOnlyString } from "../../utils/dateTimeFormatters";
import EvidenceHistoryTimelineSpacer from "./EvidenceHistoryTimlineSpacer";
import clsx from "clsx";
import { EvidenceHistory, EvidenceHistoryGap } from "../../api/models/Evidence";
import { isEvidenceHistoryGap } from "./EvidenceHistoryTimeline";

type EvidenceHistoryTimelineItemProps = {
    isFirstItem?: boolean;
    isLastItem?: boolean;
    previousItemIsMissing: boolean;
    nextItemIsMissing: boolean;
} & (EvidenceHistory | EvidenceHistoryGap);

const borderClasses = (isMissing: boolean) =>
    isMissing
        ? "top-[2px] border-dashed border-l-red-40"
        : "border-l-surface-dark";

const EvidenceHistoryTimelineItem = (
    item: EvidenceHistoryTimelineItemProps
) => {
    const {
        isFirstItem,
        isLastItem,
        nextItemIsMissing,
        previousItemIsMissing,
    } = item;

    if (isEvidenceHistoryGap(item))
        return (
            <>
                <div className="w-full flex gap-12">
                    <div className="hidden sm:flex flex-col items-center ml-4">
                        <div className="w-8" />
                        <div className="flex-1 w-px bg-transparent border-l border-dashed border-red-40"></div>
                    </div>
                    <div className="w-full max-w-full sm:w-auto bg-red-95 border border-red-40 rounded-lg p-5 flex-1 flex items-center gap-3">
                        <Warning
                            width={20}
                            height={20}
                            className="text-red-40"
                        />
                        <Typography variant="body-large" component="p">
                            Evidence missing between{" "}
                            {formatDateOnlyString(item.fromDate)} -{" "}
                            {formatDateOnlyString(item.toDate)}
                        </Typography>
                    </div>
                </div>
                {!isLastItem && <EvidenceHistoryTimelineSpacer missing />}
            </>
        );

    return (
        <>
            <div className="w-full flex gap-12">
                <div className="hidden sm:flex flex-col items-center ml-4">
                    <div
                        className={clsx(
                            "flex-1 w-px bg-transparent border-l",
                            isFirstItem
                                ? "border-l-transparent"
                                : borderClasses(previousItemIsMissing)
                        )}
                    />
                    <div className="flex-none w-8 h-8 rounded-full flex items-center justify-center border border-surface-dark">
                        <File width={20} height={20} weight="light" />
                    </div>
                    <div
                        className={clsx(
                            "flex-1 w-px bg-transparent border-l",
                            isLastItem
                                ? "border-l-transparent"
                                : borderClasses(nextItemIsMissing)
                        )}
                    ></div>
                </div>
                <div className="w-full max-w-full sm:w-auto bg-surface-light-1 rounded-lg p-5 space-y-1 flex-1">
                    <Typography
                        variant="body-large"
                        component="p"
                        className="overflow-hidden overflow-ellipsis"
                    >
                        <b>Document URL:</b>{" "}
                        <a
                            className="underline"
                            href={item.documentUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {item.documentUrl}
                        </a>
                    </Typography>
                    <Typography variant="body-large" component="p">
                        <b>Valid from:</b>{" "}
                        {formatDateOnlyString(item.validFromDate)}
                    </Typography>
                    <Typography variant="body-large" component="p">
                        <b>Valid to:</b>{" "}
                        {formatDateOnlyString(item.validToDate)}
                    </Typography>
                </div>
            </div>
            {!isLastItem && (
                <EvidenceHistoryTimelineSpacer missing={nextItemIsMissing} />
            )}
        </>
    );
};

export default EvidenceHistoryTimelineItem;
